import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('LeanControls Error:', error, errorInfo);
    }

    private handleReload = () => {
        window.location.reload();
    };

    private handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-8">
                    <div className="max-w-md w-full bg-white rounded-[2rem] border border-zinc-200 shadow-xl p-10 text-center">
                        <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <AlertTriangle size={32} className="text-amber-500" />
                        </div>

                        <h1 className="text-xl font-black text-black uppercase tracking-tight mb-2">
                            System Fault
                        </h1>

                        <p className="text-zinc-500 text-sm mb-6">
                            An unexpected error occurred. Your data is safe.
                        </p>

                        {this.state.error && (
                            <div className="bg-zinc-50 rounded-xl p-4 mb-6 text-left">
                                <p className="text-[10px] font-black uppercase text-zinc-400 tracking-widest mb-2">Error Details</p>
                                <p className="text-xs text-zinc-600 font-mono break-all">
                                    {this.state.error.message}
                                </p>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={this.handleReset}
                                className="flex-1 py-3 px-4 bg-zinc-100 hover:bg-zinc-200 text-black rounded-xl font-black text-[10px] uppercase tracking-widest transition-all"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={this.handleReload}
                                className="flex-1 py-3 px-4 bg-black hover:bg-zinc-800 text-white rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                            >
                                <RefreshCw size={14} /> Reload
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
